import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Building2, AlertCircle } from 'lucide-react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const { loadUserProfile } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    // Load Poppins font dynamically
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Force Poppins font with maximum specificity
    const style = document.createElement('style');
    style.textContent = `
      .login-container *,
      .login-container *::before,
      .login-container *::after,
      .login-card *,
      .login-card *::before,
      .login-card *::after,
      .card *,
      .card *::before,
      .card *::after,
      .card-body *,
      .card-body *::before,
      .card-body *::after,
      h1, h2, h3, h4, h5, h6,
      p, span, div, button, input, label,
      .btn, .form-control, .form-label,
      .alert, .text-muted {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    // Also force font on specific elements by direct manipulation
    setTimeout(() => {
      const elements = document.querySelectorAll('.login-container, .login-card, .card, .card-body, h1, h2, h3, h4, h5, h6, p, span, div, button, input, label, .btn, .form-control, .form-label, .alert, .text-muted');
      elements.forEach(el => {
        el.style.setProperty('font-family', "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif", 'important');
      });
    }, 100);

    // Load saved email if "Remember Me" was previously checked
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    const savedEmail = localStorage.getItem('rememberedEmail');
    
    if (savedRememberMe && savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRememberMeChange = (e) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);
    
    // Clear saved data when unchecked
    if (!isChecked) {
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberMe');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Include rememberMe flag in login request for extended session
      const response = await apiService.login({
        ...formData,
        rememberMe: rememberMe
      });
      
      if (response.status && response.token) {
        // Handle Remember Me functionality
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberedEmail');
          localStorage.removeItem('rememberMe');
        }
        
        // Store the token
        localStorage.setItem('token', response.token);
        // Load user profile immediately and route by role
        const profile = await loadUserProfile();
        const role = profile?.role;
        if (role === 'superAdmin') {
          navigate('/dashboard');
        } else if ((role === 'companyAdmin' || role === 'user') && profile?.company?.id) {
          // Redirect companyAdmin and user roles to their company projects page
          navigate(`/company/${profile.company.id}/projects`);
        } else {
          navigate('/companies');
        }
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Inline styles for maximum compatibility
  const poppinsStyle = {
    fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 400
  };

  const headingStyle = {
    fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 600
  };

  return (
    <div className="login-container" style={poppinsStyle}>
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Row className="justify-content-center w-100">
          <Col xs={12} sm={10} md={8} lg={4} xl={4} className="d-flex justify-content-center">
            <Card className="login-card" style={poppinsStyle}>
              <Card.Body className="p-4" style={poppinsStyle}>
                <div className="text-center mb-4">
                  <div className="login-logo mb-3">
                    <Building2 size={48} />
                  </div>
                  <h2 className="mb-2" style={headingStyle}>Welcome Back</h2>
                  <p className="text-muted" style={poppinsStyle}>Sign in to your account</p>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-3" style={poppinsStyle}>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label style={poppinsStyle}>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                      style={poppinsStyle}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label style={poppinsStyle}>Password</Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                        required
                        style={poppinsStyle}
                      />
                      <Button
                        variant="outline-secondary"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={poppinsStyle}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                    </div>
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Form.Check
                      type="checkbox"
                      label="Remember me"
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                      style={poppinsStyle}
                    />
                    <Button
                      variant="link"
                      className="p-0"
                      style={poppinsStyle}
                      onClick={() => navigate('/forgot-password')}
                    >
                      Forgot Password?
                    </Button>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 mb-3"
                    disabled={isLoading}
                    style={{...poppinsStyle, fontWeight: 500}}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                  <div className="text-center">
                    <span style={poppinsStyle}>
                      Don't have an account?{' '}
                    </span>
                    <Button
                      variant="link"
                      className="p-0"
                      style={poppinsStyle}
                    >
                      Sign Up
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login; 